import type { Database } from 'node-sqlite3-wasm';
import { isCliImmediateShutdownRequested } from '../../runtime/cli-process-interrupt.js';
import { CliSqliteInterrupted, isCliSqliteLockError } from '../sqlite-errors.js';
import { logStaleSqliteLockIntervention } from '../sqlite-lock-path.js';
import { SYNC_RETRY_MAX, syncSleepMs } from '../sqlite-lock-retry.js';
import type { SqliteDatabaseAccessOptions } from '../database-access-types.js';

export function runSqliteWithSync<OpenOptions, T>(
  config: SqliteDatabaseAccessOptions<OpenOptions>,
  openConnection: (options?: OpenOptions) => Database,
  safeClose: (db: Database | null | undefined) => void,
  fn: (db: Database) => T,
  options?: OpenOptions,
): T {
  for (let attempt = 1; attempt <= SYNC_RETRY_MAX; attempt++) {
    if (isCliImmediateShutdownRequested()) throw new CliSqliteInterrupted();
    let db: Database | undefined;
    try {
      db = openConnection(options);
      try {
        return fn(db);
      } finally {
        safeClose(db);
      }
    } catch (e) {
      safeClose(db);
      if (e instanceof CliSqliteInterrupted) throw e;
      if (!isCliSqliteLockError(e)) throw e;
      config.onLockRetry?.({
        dbPath: config.getPath(),
        attempt,
        maxAttempts: SYNC_RETRY_MAX,
        final: attempt === SYNC_RETRY_MAX,
      });
      if (attempt === SYNC_RETRY_MAX) {
        logStaleSqliteLockIntervention(config.getPath());
        throw e;
      }
      syncSleepMs(Math.min(500, 12 * attempt));
    }
  }
  throw new Error('withSqliteSync: exhausted retries');
}
